// 数据恢复工具 - 帮助恢复被覆盖的用户故事数据
export class DataRecoveryTool {
  constructor() {
    this.recoveryPrefix = 'altverse-recovery-'
    this.backupPrefix = 'altverse-backup-'
  }

  // 检查是否有可恢复的数据
  checkRecoveryData(storyId) {
    const recoveryKey = `${this.recoveryPrefix}${storyId}`
    const backupKey = `${this.backupPrefix}${storyId}`
    const userEditedKey = `user-edited-${storyId}`
    
    const recoveryData = localStorage.getItem(recoveryKey)
    const backupData = localStorage.getItem(backupKey)
    const userEditedData = localStorage.getItem(userEditedKey)
    
    return {
      hasRecovery: !!recoveryData,
      hasBackup: !!backupData,
      hasUserEdited: !!userEditedData,
      recoveryKey,
      backupKey,
      userEditedKey
    }
  }

  // 创建数据备份
  createBackup(storyId, storyData) {
    try {
      const backupKey = `${this.backupPrefix}${storyId}`
      const backupData = {
        ...storyData,
        backupTime: new Date().toISOString(),
        backupType: 'user-created'
      }
      
      localStorage.setItem(backupKey, JSON.stringify(backupData))
      console.log(`✅ 已创建数据备份: ${backupKey}`)
      return true
    } catch (error) {
      console.error('❌ 创建备份失败:', error)
      return false
    }
  }

  // 恢复用户数据
  async recoverUserData(storyId) {
    const recoveryInfo = this.checkRecoveryData(storyId)
    
    if (!recoveryInfo.hasRecovery && !recoveryInfo.hasBackup && !recoveryInfo.hasUserEdited) {
      throw new Error('没有找到可恢复的数据')
    }

    let recoveredData = null
    let recoverySource = ''

    // 优先级：用户编辑数据 > 备份数据 > 恢复数据
    if (recoveryInfo.hasUserEdited) {
      try {
        recoveredData = JSON.parse(localStorage.getItem(recoveryInfo.userEditedKey))
        recoverySource = '用户编辑数据'
        console.log('✅ 从用户编辑数据恢复')
      } catch (error) {
        console.warn('⚠️ 用户编辑数据解析失败:', error)
      }
    }

    if (!recoveredData && recoveryInfo.hasBackup) {
      try {
        recoveredData = JSON.parse(localStorage.getItem(recoveryInfo.backupKey))
        recoverySource = '备份数据'
        console.log('✅ 从备份数据恢复')
      } catch (error) {
        console.warn('⚠️ 备份数据解析失败:', error)
      }
    }

    if (!recoveredData && recoveryInfo.hasRecovery) {
      try {
        recoveredData = JSON.parse(localStorage.getItem(recoveryInfo.recoveryKey))
        recoverySource = '恢复数据'
        console.log('✅ 从恢复数据恢复')
      } catch (error) {
        console.warn('⚠️ 恢复数据解析失败:', error)
      }
    }

    if (!recoveredData) {
      throw new Error('所有恢复数据都无效')
    }

    return {
      data: recoveredData,
      source: recoverySource,
      recoveryTime: new Date().toISOString()
    }
  }

  // 扫描所有可能的数据源
  scanAllDataSources() {
    const dataSources = []
    
    // 扫描localStorage中的所有相关数据
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key) continue
      
      if (key.startsWith('user-edited-') || 
          key.startsWith('ai-story-') || 
          key.startsWith('altverse-') ||
          key.startsWith(this.recoveryPrefix) ||
          key.startsWith(this.backupPrefix)) {
        
        try {
          const data = JSON.parse(localStorage.getItem(key))
          const dataSize = new Blob([JSON.stringify(data)]).size
          
          dataSources.push({
            key,
            type: this.getDataType(key),
            size: dataSize,
            sizeKB: (dataSize / 1024).toFixed(2),
            lastModified: data.lastEdited || data.updatedAt || data.backupTime || '未知',
            hasNodes: !!(data.nodes && data.nodes.length > 0),
            hasEdges: !!(data.edges && data.edges.length > 0),
            nodeCount: data.nodes ? data.nodes.length : 0,
            edgeCount: data.edges ? data.edges.length : 0,
            title: data.title || '未命名',
            storyId: this.extractStoryId(key)
          })
        } catch (error) {
          console.warn(`解析数据失败: ${key}`, error)
        }
      }
    }
    
    return dataSources.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified))
  }

  // 获取数据类型
  getDataType(key) {
    if (key.startsWith('user-edited-')) return '用户编辑数据'
    if (key.startsWith('ai-story-')) return 'AI生成数据'
    if (key.startsWith(this.recoveryPrefix)) return '恢复数据'
    if (key.startsWith(this.backupPrefix)) return '备份数据'
    if (key.startsWith('altverse-')) return '系统数据'
    return '未知类型'
  }

  // 提取故事ID
  extractStoryId(key) {
    const match = key.match(/(?:user-edited-|ai-story-|altverse-.*?-)(.+?)(?:-|$)/)
    return match ? match[1] : '未知'
  }

  // 恢复特定数据源
  async restoreFromSource(sourceKey) {
    try {
      const data = JSON.parse(localStorage.getItem(sourceKey))
      
      if (!data) {
        throw new Error('数据源不存在')
      }

      // 验证数据完整性
      if (!data.nodes || !Array.isArray(data.nodes)) {
        throw new Error('数据格式无效：缺少节点数据')
      }

      return {
        success: true,
        data: data,
        message: `成功从 ${sourceKey} 恢复数据`
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: `从 ${sourceKey} 恢复失败: ${error.message}`
      }
    }
  }

  // 创建恢复点
  createRecoveryPoint(storyId, storyData) {
    try {
      const recoveryKey = `${this.recoveryPrefix}${storyId}`
      const recoveryData = {
        ...storyData,
        recoveryTime: new Date().toISOString(),
        recoveryType: 'manual'
      }
      
      localStorage.setItem(recoveryKey, JSON.stringify(recoveryData))
      console.log(`✅ 已创建恢复点: ${recoveryKey}`)
      return true
    } catch (error) {
      console.error('❌ 创建恢复点失败:', error)
      return false
    }
  }

  // 清理过期数据
  cleanupExpiredData(daysToKeep = 7) {
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep)
    
    let cleanedCount = 0
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key) continue
      
      if (key.startsWith(this.recoveryPrefix) || key.startsWith(this.backupPrefix)) {
        try {
          const data = JSON.parse(localStorage.getItem(key))
          const dataTime = new Date(data.recoveryTime || data.backupTime || data.lastEdited)
          
          if (dataTime < cutoffDate) {
            localStorage.removeItem(key)
            cleanedCount++
            console.log(`🗑️ 已清理过期数据: ${key}`)
          }
        } catch (error) {
          // 如果解析失败，也删除
          localStorage.removeItem(key)
          cleanedCount++
          console.log(`🗑️ 已清理无效数据: ${key}`)
        }
      }
    }
    
    console.log(`✅ 清理完成，删除了 ${cleanedCount} 个过期数据项`)
    return cleanedCount
  }

  // 导出恢复报告
  generateRecoveryReport(storyId) {
    const dataSources = this.scanAllDataSources()
    const storySources = dataSources.filter(source => source.storyId === storyId)
    
    const report = {
      storyId,
      scanTime: new Date().toISOString(),
      totalSources: dataSources.length,
      storySources: storySources.length,
      sources: storySources,
      recommendations: this.generateRecommendations(storySources)
    }
    
    return report
  }

  // 生成恢复建议
  generateRecommendations(sources) {
    const recommendations = []
    
    if (sources.length === 0) {
      recommendations.push('没有找到可恢复的数据源')
      return recommendations
    }
    
    // 按类型分组
    const byType = sources.reduce((acc, source) => {
      if (!acc[source.type]) acc[source.type] = []
      acc[source.type].push(source)
      return acc
    }, {})
    
    // 推荐最佳恢复源
    if (byType['用户编辑数据'] && byType['用户编辑数据'].length > 0) {
      const latest = byType['用户编辑数据'].sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified))[0]
      recommendations.push(`推荐使用最新的用户编辑数据: ${latest.key} (${latest.lastModified})`)
    } else if (byType['备份数据'] && byType['备份数据'].length > 0) {
      const latest = byType['备份数据'].sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified))[0]
      recommendations.push(`推荐使用最新的备份数据: ${latest.key} (${latest.lastModified})`)
    } else if (byType['恢复数据'] && byType['恢复数据'].length > 0) {
      const latest = byType['恢复数据'].sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified))[0]
      recommendations.push(`推荐使用最新的恢复数据: ${latest.key} (${latest.lastModified})`)
    }
    
    // 数据质量建议
    const hasNodes = sources.some(s => s.hasNodes)
    const hasEdges = sources.some(s => s.hasEdges)
    
    if (!hasNodes) {
      recommendations.push('⚠️ 警告：没有找到包含节点数据的源')
    }
    if (!hasEdges) {
      recommendations.push('⚠️ 警告：没有找到包含连接数据的源')
    }
    
    return recommendations
  }
}

// 创建单例实例
export const dataRecoveryTool = new DataRecoveryTool()
